import type { ComponentProps, ReactNode } from 'react';
import { StyleSheet, type UnistylesThemes } from 'react-native-unistyles';
import FooterActionButton from '@/components/Custom/FooterActionButton';
import Box from '@/components/Unistyles/Box';
import Icon from '@/components/Unistyles/Icon';
import Pressable from '@/components/Unistyles/Pressable';
import Text from '@/components/Unistyles/Text';

type WelcomeTone = 'primary' | 'success' | 'info' | 'warning' | 'error';
type WelcomeTheme = UnistylesThemes['dark'];

type WelcomeIconName = ComponentProps<typeof Icon>['name'];
type WelcomeCardProps = Omit<
  ComponentProps<typeof Box>,
  'children' | 'p' | 'space' | 'style'
> & {
  children: ReactNode;
  selected?: boolean;
  tone?: WelcomeTone;
};

type WelcomeIconBadgeProps = {
  icon: WelcomeIconName;
  selected?: boolean;
  tone?: WelcomeTone;
};

type WelcomeChoiceCardProps = {
  detail?: string;
  icon: WelcomeIconName;
  onPress: ComponentProps<typeof Pressable>['onPress'];
  selected?: boolean;
  subtitle: string;
  title: string;
  tone?: WelcomeTone;
};

type WelcomeChipProps = {
  children: ReactNode;
  onPress: ComponentProps<typeof Pressable>['onPress'];
  selected?: boolean;
  tone?: WelcomeTone;
};

export function WelcomeActions({ children }: { children: ReactNode }) {
  return (
    <Box
      direction="horizontal"
      space="sm"
      center
      flexWrap="wrap"
      style={styles.actions}
    >
      {children}
    </Box>
  );
}

export function WelcomeCard({
  children,
  selected = false,
  tone = 'primary',
  ...boxProps
}: WelcomeCardProps) {
  return (
    <Box {...boxProps} p="lg" space="md" style={styles.card(selected, tone)}>
      {children}
    </Box>
  );
}

export function WelcomeChoiceCard({
  detail,
  icon,
  onPress,
  selected = false,
  subtitle,
  title,
  tone = 'primary',
}: WelcomeChoiceCardProps) {
  return (
    <Pressable onPress={onPress} style={styles.choicePressable}>
      <Box
        direction="horizontal"
        alignItems="center"
        space="md"
        p="lg"
        style={styles.choiceCard(selected, tone)}
      >
        <WelcomeIconBadge icon={icon} selected={selected} tone={tone} />
        <Box flex space="xs">
          <Box direction="horizontal" alignItems="center" space="sm">
            <Text
              flexShrink={1}
              size="xl"
              bold
              variant={selected ? tone : 'typography'}
            >
              {title}
            </Text>
            {detail ? (
              <Text size="xs" bold variant={tone} style={styles.detailPill}>
                {detail}
              </Text>
            ) : null}
          </Box>
          <Text size="md" variant="typographySecondary">
            {subtitle}
          </Text>
        </Box>
        <Icon name="chevron-forward" size="sm" variant="typographySecondary" />
      </Box>
    </Pressable>
  );
}

export function WelcomeChip({
  children,
  onPress,
  selected = false,
  tone = 'primary',
}: WelcomeChipProps) {
  return (
    <Pressable onPress={onPress}>
      <Box px="md" py="sm" style={styles.chip(selected, tone)}>
        <Text size="sm" bold variant={selected ? tone : 'typographySecondary'}>
          {children}
        </Text>
      </Box>
    </Pressable>
  );
}

export function WelcomeIconBadge({
  icon,
  selected = false,
  tone = 'primary',
}: WelcomeIconBadgeProps) {
  return (
    <Box center style={styles.iconBadge(selected, tone)}>
      <Icon name={icon} size="xl" variant={selected ? 'white' : tone} />
    </Box>
  );
}

export function WelcomeNotice({
  children,
  icon = 'information-circle-outline',
  tone = 'info',
}: {
  children: ReactNode;
  icon?: WelcomeIconName;
  tone?: WelcomeTone;
}) {
  return (
    <Box
      direction="horizontal"
      alignItems="center"
      space="sm"
      style={styles.notice(tone)}
    >
      <WelcomeIconBadge icon={icon} tone={tone} />
      <Box flex>{children}</Box>
    </Box>
  );
}

export function WelcomePrimaryAction({
  style,
  testID = 'FormSaveButton',
  ...props
}: ComponentProps<typeof FooterActionButton>) {
  return (
    <FooterActionButton
      variant="success"
      testID={testID}
      {...props}
      containerStyle={styles.primaryAction}
      style={style}
    />
  );
}

export function WelcomeSecondaryAction({
  style,
  ...props
}: ComponentProps<typeof FooterActionButton>) {
  return (
    <FooterActionButton
      variant="secondary"
      appearance="outline"
      {...props}
      containerStyle={styles.primaryAction}
      style={style}
    />
  );
}

function getToneColor(theme: WelcomeTheme, tone: WelcomeTone) {
  switch (tone) {
    case 'error':
      return theme.colors.error;
    case 'info':
      return theme.colors.info;
    case 'success':
      return theme.colors.success;
    case 'warning':
      return theme.colors.warning;
    default:
      return theme.colors.primary;
  }
}

function getToneSurface(theme: WelcomeTheme) {
  return theme.colors.surface === theme.colors.backgroundLight0
    ? theme.colors.background
    : theme.colors.surfaceSecondary;
}

const styles = StyleSheet.create((theme) => ({
  primaryAction: {
    flexGrow: 1,
    flexShrink: 1,
    margin: 0,
  },
  actions: {
    flexWrap: 'wrap',
    justifyContent: 'center',
    width: '100%',
  },
  card: (selected: boolean, tone: WelcomeTone) => ({
    backgroundColor: selected ? getToneSurface(theme) : theme.colors.surface,
    borderColor: selected
      ? getToneColor(theme, tone)
      : theme.colors.borderMuted,
    borderCurve: 'continuous',
    borderRadius: theme.borderRadius.xl,
    borderWidth: theme.size(0.5),
    width: '100%',
  }),
  choiceCard: (selected: boolean, tone: WelcomeTone) => ({
    backgroundColor: selected ? getToneSurface(theme) : theme.colors.surface,
    borderColor: selected
      ? getToneColor(theme, tone)
      : theme.colors.borderMuted,
    borderCurve: 'continuous',
    borderRadius: theme.borderRadius.xl,
    borderWidth: theme.size(0.5),
    minHeight: theme.size(20),
    width: '100%',
  }),
  choicePressable: {
    borderCurve: 'continuous',
    borderRadius: theme.borderRadius.xl,
    overflow: 'hidden',
    width: '100%',
  },
  chip: (selected: boolean, tone: WelcomeTone) => ({
    backgroundColor: selected ? getToneSurface(theme) : theme.colors.surface,
    borderColor: selected
      ? getToneColor(theme, tone)
      : theme.colors.borderMuted,
    borderCurve: 'continuous',
    borderRadius: theme.borderRadius.full,
    borderWidth: theme.size(0.5),
  }),
  detailPill: {
    backgroundColor: theme.colors.surface,
    borderColor: theme.colors.borderMuted,
    borderCurve: 'continuous',
    borderRadius: theme.borderRadius.full,
    borderWidth: theme.size(0.5),
    overflow: 'hidden',
    paddingHorizontal: theme.space('sm'),
    paddingVertical: theme.space('xs'),
  },
  iconBadge: (selected: boolean, tone: WelcomeTone) => ({
    backgroundColor: selected
      ? getToneColor(theme, tone)
      : getToneSurface(theme),
    borderColor: selected
      ? getToneColor(theme, tone)
      : theme.colors.borderMuted,
    borderWidth: theme.size(0.5),
    borderCurve: 'continuous',
    borderRadius: theme.borderRadius.xl,
    height: theme.size(11),
    width: theme.size(11),
  }),
  notice: (tone: WelcomeTone) => ({
    backgroundColor: getToneSurface(theme),
    borderColor: getToneColor(theme, tone),
    borderCurve: 'continuous',
    borderRadius: theme.borderRadius.xl,
    borderWidth: theme.size(0.5),
    padding: theme.space('md'),
    width: '100%',
  }),
}));
